import { createContext, useContext, useEffect, useState } from 'react';
import { useChannel } from '../Channel/ChannelProvider';
import { Measure, MeasureUnit } from '@formify/projects-state-manager';

interface MeasureUnitProviderContextValues {
    availableMeasureUnit: MeasureUnit[];
    currentMeasureUnit: MeasureUnit;
    setMeasureUnit: (measureUnitCode: MeasureUnit) => void;
    format: (value: Measure) => [string, string];
}

const measureUnitContext = createContext<MeasureUnitProviderContextValues>({
    availableMeasureUnit: [],
    currentMeasureUnit: 'MM',
    setMeasureUnit: () => {},
    format: () => ['0', 'MM'],
});

export const useMeasureUnit = () => useContext(measureUnitContext);

interface MeasureUnitProviderProps {
    children: React.ReactNode;
}

const fractionStringToAscii: {
    [key: string]: string;
} = {
    '1/16': '¹⁄₁₆',
    '1/8': '⅛',
    '3/16': '³⁄₁₆',
    '1/4': '¼',
    '5/16': '⁵⁄₁₆',
    '3/8': '⅜',
    '7/16': '⁷⁄₁₆',
    '1/2': '½',
    '9/16': '⁹⁄₁₆',
    '5/8': '⅝',
    '11/16': '¹¹⁄₁₆',
    '3/4': '¾',
    '13/16': '¹³⁄₁₆',
    '7/8': '⅞',
    '15/16': '¹⁵⁄₁₆',
};

const replaceFractionString = (s: string) => fractionStringToAscii[s] || s;
const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));

export const formatMeasurement = (value: Measure, unit: MeasureUnit): [string, string] => {
    if (unit === 'MM') {
        const valueInMM = value.getInteger('MM');

        return [`${valueInMM / 10}`, 'cm'];
    }

    if (unit === '16_INCH') {
        const integerValue = value.getInteger('16_INCH');
        const inch = Math.floor(integerValue / 16);
        const inchRest = integerValue % 16;

        if (inchRest === 0) {
            return [`${inch}`, '"'];
        } else {
            const denominator = 16;
            const divisor = gcd(inchRest, denominator);
            const numerator = inchRest / divisor;
            const simplifiedDenominator = denominator / divisor;
            const fractionPart = replaceFractionString(`${numerator}/${simplifiedDenominator}`);

            if (inch === 0) {
                return [fractionPart, '"'];
            } else {
                return [`${inch}\xA0${fractionPart}`, '"'];
            }
        }
    }

    return [value.getInches().toFixed(1), 'in'];
};

export const MeasureUnitProvider = ({ children }: MeasureUnitProviderProps) => {
    const { defaultUnit, handledUnits } = useChannel();
    const [currentMeasureUnit, setMeasureUnit] = useState<MeasureUnit>(defaultUnit);

    useEffect(() => {
        setMeasureUnit(defaultUnit);
    }, [defaultUnit]);

    useEffect(() => {
        setMeasureUnit((currentUnit) => {
            if (handledUnits.includes(currentUnit)) {
                return currentUnit;
            }

            return handledUnits[0] ?? currentUnit;
        });
    }, [handledUnits, defaultUnit]);

    return (
        <measureUnitContext.Provider
            value={{
                availableMeasureUnit: handledUnits,
                currentMeasureUnit,
                setMeasureUnit,
                format: (value: Measure) => formatMeasurement(value, currentMeasureUnit),
            }}
        >
            {children}
        </measureUnitContext.Provider>
    );
};
